import React, { useState, useEffect } from "react"
import useLockBodyScroll from "../hooks/useLockBodyScroll.js"
import CustomSelect from "./CustomSelect.jsx"

const CONTACT_ENDPOINT = "https://forms.2merative.com/api/2merative-contact.php?form=google-profile-review"
const MIN_HUMAN_DELAY_MS = 2000

function normalizeUrl(raw) {
  if (!raw) return ""
  let v = raw.trim()
  if (!/^https?:\/\//i.test(v)) v = `https://${v}`
  return v
}

function isValidUrl(url) {
  try {
    new URL(url)
    return true
  } catch {
    return false
  }
}

export default function GoogleProfileReviewCTA({
  buttonLabel = "Google Profile Boost $149",
  className = "",
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState("")
  const [openedAt, setOpenedAt] = useState(null)

  useLockBodyScroll(isOpen)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get("google-review") === "1") {
      setIsOpen(true)
      setOpenedAt(Date.now())
    }
  }, [])

  const open = () => {
    setIsOpen(true)
    setSubmitted(false)
    setErr("")
    setOpenedAt(Date.now())
  }

  const close = () => {
    setIsOpen(false)
    setSubmitted(false)
    setErr("")
    setOpenedAt(null)
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setErr("")

    const formData = new FormData(e.target)
    const payload = Object.fromEntries(formData.entries())

    if (payload._gotcha && payload._gotcha.trim() !== "") {
      setSubmitted(true)
      setLoading(false)
      setTimeout(close, 2000)
      return
    }

    if (openedAt && Date.now() - openedAt < MIN_HUMAN_DELAY_MS) {
      setSubmitted(true)
      setLoading(false)
      setTimeout(close, 2000)
      return
    }

    payload.website = normalizeUrl(payload.website)

    if (payload.website && !isValidUrl(payload.website)) {
      setLoading(false)
      setErr("Please enter a valid website URL (e.g. https://example.com)")
      return
    }

    payload.offer = "2026 Kick-off Google Business Profile Review ($149)"
    payload._subject = "New enquiry: Google Business Profile Review ($149)"

    try {
      const res = await fetch(CONTACT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })

      if (res.ok) {
        setSubmitted(true)
        setTimeout(close, 2000)
      } else {
        throw new Error("Submission failed")
      }
    } catch (e2) {
      console.error(e2)
      setErr("Something went wrong submitting the form. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <button
        onClick={open}
        className={`bg-orange-500 text-white px-5 py-3 rounded-full shadow-lg text-sm sm:text-base md:text-lg hover:bg-orange-600 transition ${className}`}
      >
        {buttonLabel}
      </button>

      {isOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[2147483647]">
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl w-full max-w-md relative shadow-2xl max-h-[85vh] overflow-y-auto overscroll-contain">
              <div className="p-6">
                <button
                  onClick={close}
                  className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 text-2xl leading-none"
                  aria-label="Close"
                >
                  &times;
                </button>

                {!submitted ? (
                  <>
                    <div className="mb-4">
                      <div className="text-xs font-semibold tracking-wide text-orange-600 uppercase">
                        2026 Kick-off Offer
                      </div>
                      <h3 className="text-lg font-semibold text-slate-900 mt-1">
                        Google Business Profile Review and Optimisation
                      </h3>
                      <p className="text-sm text-slate-600 mt-2">
                        One-time fee: <span className="font-semibold">$149</span>. Improve local
                        visibility, trust, and enquiries from Google Search and Maps.
                      </p>
                    </div>

                    {err && (
                      <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 p-2 rounded">
                        {err}
                      </div>
                    )}

                    <form className="space-y-4" onSubmit={onSubmit}>
                      <input
                        type="text"
                        name="_gotcha"
                        tabIndex={-1}
                        autoComplete="off"
                        style={{ display: "none" }}
                      />

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <input
                          type="text"
                          name="name"
                          placeholder="Your name"
                          required
                          className="w-full border border-gray-300 rounded-lg p-2"
                        />
                        <input
                          type="text"
                          name="business"
                          placeholder="Business name"
                          required
                          className="w-full border border-gray-300 rounded-lg p-2"
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <input
                          type="email"
                          name="email"
                          placeholder="Email"
                          required
                          className="w-full border border-gray-300 rounded-lg p-2"
                        />
                        <input
                          type="tel"
                          name="phone"
                          placeholder="Phone"
                          className="w-full border border-gray-300 rounded-lg p-2"
                        />
                      </div>

                      <input
                        type="text"
                        name="website"
                        placeholder="Website (optional)"
                        className="w-full border border-gray-300 rounded-lg p-2"
                      />

                      <input
                        type="text"
                        name="suburb"
                        placeholder="Suburb / service area"
                        className="w-full border border-gray-300 rounded-lg p-2"
                      />

                      <CustomSelect
                        name="gbp_access"
                        defaultValue="unsure"
                        options={[
                          { value: "yes", label: "I can access my Google Business Profile" },
                          { value: "no", label: "I do not have access right now" },
                          { value: "unsure", label: "Not sure" },
                        ]}
                      />

                      <textarea
                        name="notes"
                        placeholder="Anything you want us to know (optional)"
                        rows={4}
                        className="w-full border border-gray-300 rounded-lg p-2"
                      />

                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 transition disabled:opacity-70"
                      >
                        {loading ? "Submitting..." : "Book my $149 review"}
                      </button>
                    </form>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <h3 className="text-lg font-semibold mb-2 text-green-700">
                      Booked request received
                    </h3>
                    <p className="text-slate-600">We have your details. We will be in touch shortly.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
